import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PeopleTwoTone } from '@mui/icons-material';
import { Box, Stack, RadioGroup, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { boolean, object, string, type StringSchema } from 'yup';
import { tuitionAssistantshipOptions } from '../options';
import { DSRC_DECISION_TYPE, OFFER_TYPE } from '../constants';
import { PAGE_TEXT } from '../../../lang';
import {
  Dialog,
  Alert,
  Select,
  Radio,
  TextField,
  Checkbox,
  Button,
  ButtonContainer,
  type AlertDetails,
  Loading,
  Accordion,
} from '../../../../../components';
import { handleReadySubmit } from '../../../../../utils';
import { QualificationCheck } from './QualificationCheck';
import {
  useGetAdmissionStepData,
  useCreateAdmissionStepData,
  useUpdateAdmissionStepData,
} from './graphql';
import { AdmissionDecision } from './AdmissionDecision';
import type { AdmissionForm, Conditions } from './types';
import { useAuth } from '../../../../../contexts';
import {
  PAGE_TEXT as PRE_CHECK_TEXT,
  programmeOptions,
} from '../../../../Nomination/components';
import {
  EntranceRequirement,
  TuitionAssistantship,
} from '../../../../../graphql/graphql';
import { RadioWithTextField } from './RadioWithTextField';

type AdmissionProps = {
  entryYear: string;
  isViewMode?: boolean;
  onJumpToNext?: () => void;
};

const ADMISSION_FORM_TEXT = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_G;

const commonSvgIconStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  '& .MuiSvgIcon-root': {
    marginTop: '-0.2rem',
    fontSize: 18,
  },
};

const commonInsertTextFieldStyle = {
  margin: '-0.5rem 0.5rem 0',
};

export const Admission: React.FunctionComponent<AdmissionProps> = props => {
  const { isViewMode = false, entryYear, onJumpToNext } = props;
  const { applicationId = '' } = useParams();
  const { handleLoading } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const {
    qualificationChecks,
    suggestOffers,
    nominatedProgramme,
    requestRequirementWaive,
    programmeRequirements,
    maxMeetConditionDeadline,
    admissionForm,
    needStipend,
    hasCreated,
    loading,
    error,
  } = useGetAdmissionStepData(
    applicationId,
    entryYear,
    {
      application: { _id: { eq: applicationId } },
    },
    { applicationId },
  );

  const handleValidationBeforeSubmission = async (
    value: AdmissionForm,
  ): Promise<void> => {
    const { admissionDecision, offerType } = value;
    if (admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED) {
      const prgVal = programmeOptions.find(
        prg => prg?.label === nominatedProgramme,
      )?.value;
      // no suggested offer
      if (!suggestOffers.length) {
        setOpenDialog(true);
      } else {
        // has suggested offer
        // should check both the prog (by supervisor) and the offer type(by DoRPgS)
        if (suggestOffers[0].offerType !== null) {
          const matchedOffer = suggestOffers.find(
            item =>
              item.programme === prgVal &&
              ((item.offerType === OFFER_TYPE.CONDITION &&
                offerType === OFFER_TYPE.CONDITION) ||
                (item.offerType === OFFER_TYPE.FIRM &&
                  offerType === OFFER_TYPE.FIRM)),
          );
          // not match
          if (!matchedOffer) {
            setOpenDialog(true);
          } else {
            // suggested and match, submit without confirmation
            await handleSubmit(value);
          }
        }
      }
    } else {
      await handleSubmit(value);
    }
  };

  const requiredIfNotFirmOffer = (schema: StringSchema): StringSchema =>
    schema.when(['offerType', 'admissionDecision'], {
      is: (offerType: OFFER_TYPE, admissionDecision: string) =>
        offerType === OFFER_TYPE.CONDITION &&
        admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED,
      then: schema => schema.required('Required'),
      otherwise: schema => schema.notRequired(),
    });

  const conditionSchema = object().shape({
    detail: string().when(['ticked', 'detailType'], {
      is: (
        ticked: boolean | undefined,
        detailType: 'custom' | 'default' | undefined,
      ) => ticked === true && detailType === 'custom',
      then: schema => schema.required('Detail is required'),
      otherwise: schema => schema.notRequired(),
    }),
    ticked: boolean().when(['$offerType', '$admissionDecision'], {
      is: (offerType: OFFER_TYPE, admissionDecision: string) =>
        offerType === OFFER_TYPE.CONDITION &&
        admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED,
      then: schema =>
        schema.test(
          'at-least-one-ticked',
          'At least one condition must be ticked',
          (value, ctx) => {
            const validationValue = ctx.from?.[1].value;
            return (
              validationValue &&
              typeof validationValue === 'object' &&
              Object.values(validationValue as Conditions).some(
                item => item?.ticked,
              )
            );
          },
        ),
      otherwise: schema => schema.notRequired(),
    }),
  });

  const formik = useFormik<AdmissionForm>({
    initialValues: admissionForm as AdmissionForm,
    enableReinitialize: true,
    initialStatus: loading ?? true,
    validationSchema: object({
      admissionDecision: string().required('Required'),
      returnDestination: string().when('admissionDecision', {
        is: (admissionDecision: string) =>
          admissionDecision === DSRC_DECISION_TYPE.RETURN,
        then: schema => schema.required('Required'),
        otherwise: schema => schema.notRequired(),
      }),
      remarks: string().when('admissionDecision', {
        is: (admissionDecision: string) =>
          admissionDecision === DSRC_DECISION_TYPE.RETURN,
        then: schema => schema.required('Required'),
        otherwise: schema => schema.notRequired(),
      }),
      entranceRequirementWaive: boolean().when('admissionDecision', {
        is: (admissionDecision: string) =>
          admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED &&
          requestRequirementWaive,
        then: schema => schema.required(), // show empty helper text
        otherwise: schema => schema.notRequired(),
      }),
      offerType: string().when('admissionDecision', {
        is: (admissionDecision: string) =>
          admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED,
        then: schema => schema.required('Required'),
        otherwise: schema => schema.notRequired(),
      }),
      conditions: object().shape(
        Object.values(EntranceRequirement).reduce((acc, value) => {
          return {
            ...acc,
            [value]: conditionSchema,
          };
        }, {}),
      ),
      contactDepartmentPersonName: string().when(
        ['offerType', 'admissionDecision'],
        {
          is: (offerType: OFFER_TYPE, admissionDecision: string) =>
            offerType === OFFER_TYPE.CONDITION &&
            admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED,
          then: schema => schema.required('Required'),
          otherwise: schema => schema.notRequired(),
        },
      ),
      contactDepartmentPersonEmail: requiredIfNotFirmOffer(
        string().email('Invalid email'),
      ),
      contactDepartmentPersonPhone: requiredIfNotFirmOffer(string()),
      conditionFulfillingDeadline: requiredIfNotFirmOffer(string()),
      tuitionAssistantshipType: string().when('admissionDecision', {
        is: (admissionDecision: string) =>
          admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED,
        then: schema => schema.required('Required'),
        otherwise: schema => schema.notRequired(),
      }),
      rejectionRemarks: string().nullable(),
      tuitionFeeAccountCode: string().when(
        ['admissionDecision', 'tuitionAssistantshipType'],
        {
          is: (admissionDecision: string, tuitionAssistantshipType: string) =>
            admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED &&
            tuitionAssistantshipType !== TuitionAssistantship.Na,
          then: schema => schema.required('Required'),
          otherwise: schema => schema.notRequired(),
        },
      ),

      stipendApproval: boolean().when('admissionDecision', {
        is: (admissionDecision: string) =>
          admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED &&
          needStipend,
        then: schema => schema.required(), // show empty helper text
        otherwise: schema => schema.notRequired(),
      }),

      plagiarismCheck: boolean().when('admissionDecision', {
        is: (admissionDecision: string) =>
          admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED,
        then: schema => schema.oneOf([true]),
        otherwise: schema => schema.notRequired(),
      }),
      plagiarismNotFound: boolean().when('admissionDecision', {
        is: (admissionDecision: string) =>
          admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED,
        then: schema => schema.oneOf([true]),
        otherwise: schema => schema.notRequired(),
      }),

      confirmationChairInTeam: boolean().required(),
      confirmationDoRPgSInTeam: boolean().required(),
    }),
    onSubmit: handleValidationBeforeSubmission,
  });

  const isApproved =
    formik.values.admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED;
  const provideTuition =
    formik.values.tuitionAssistantshipType !== TuitionAssistantship.Na;

  const {
    createAdmissionStepData,
    convertAdmissionForm: convertCreateAdmissionForm,
  } = useCreateAdmissionStepData();
  const {
    updateAdmissionStepData,
    convertAdmissionForm: convertUpdateAdmissionForm,
  } = useUpdateAdmissionStepData();

  const handleSubmit = async (values: AdmissionForm): Promise<void> => {
    /** final save */
    let alertDetailDef = alertDetails;
    await handleLoading(async () => {
      try {
        if (!hasCreated) {
          const variables = {
            input: convertCreateAdmissionForm(applicationId, values),
          };
          await createAdmissionStepData({
            variables,
          });
          alertDetailDef = {
            alertContent: 'Saved Admission Requirement Successfully.',
            alertType: 'success',
          };
        } else {
          const variables = {
            input: convertUpdateAdmissionForm(applicationId, values),
          };
          await updateAdmissionStepData({ variables });
          alertDetailDef = {
            alertContent: 'Updated Admission Requirement Successfully.',
            alertType: 'success',
          };
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error: any) {
        alertDetailDef = {
          alertContent: `Error Occurs.${(error as Error).message}`,
          alertType: 'error',
        };
      }
    });
    setAlertDetails(alertDetailDef);
    setOpenAlert(true);
    setOpenDialog(false);
  };

  const handleCancelSubmit = (): void => {
    setOpenDialog(false);
  };

  const handleConfirmSubmit = async (): Promise<void> => {
    /** final save */
    await handleSubmit(formik.values);
  };

  if (error as boolean) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + `${(error as Error)?.message}`,
          alertType: 'error',
        }}
      />
    );
  }

  // loading's status is not reliable here
  // when loading is false, the initialization of formik is not completed
  // and the form components(checkboxs) will render with undefined values
  // when formik completed the initialization, form components will not be re-rendered
  // and will display wrong values
  if (formik.status as boolean) {
    return <Loading />;
  }

  return (
    <div className="w-full">
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Dialog
        openDialog={openDialog}
        dialogTitle={PRE_CHECK_TEXT.GSB1_FORM.SUBMIT_CONFIRM.TITLE}
        dialogAction={PAGE_TEXT.SUBMIT}
        cancelAction={PAGE_TEXT.CANCEL}
        maxWidth={'sm'}
        onDialogAction={handleConfirmSubmit}
        onDialogClose={handleCancelSubmit}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {PRE_CHECK_TEXT.GSB1_FORM.SUBMIT_CONFIRM.CONTENT}
        </Typography>
      </Dialog>
      <Accordion
        className="w-full"
        defaultExpanded
        disableExpand
        title={ADMISSION_FORM_TEXT.TITLE}
        details={
          <form
            className="!text-sm"
            onSubmit={handleReadySubmit(formik)}
            noValidate
          >
            <Stack padding={2}>
              <QualificationCheck qualificationChecks={qualificationChecks} />
              <Box className="my-4">
                <PeopleTwoTone sx={{ color: '#951D1D' }} />
                <span className="ml-2">
                  <span className="font-bold text-[#951D1D]">
                    {ADMISSION_FORM_TEXT.ADMISSION.SUPERVISOR_DECISION}
                    :&nbsp;&nbsp;
                  </span>
                  <b>{nominatedProgramme}</b>
                </span>
              </Box>
              {/* admission */}
              <Box bgcolor={'#F6F6F6'} paddingX={'0.75rem'}>
                <AdmissionDecision
                  isViewMode={isViewMode}
                  requestRequirementWaive={requestRequirementWaive}
                  programmeRequirements={programmeRequirements}
                  maxMeetConditionDeadline={maxMeetConditionDeadline}
                  formik={formik}
                />
                {/* 2.Tuition Fee Assistantship */}
                {isApproved && (
                  <Box>
                    <h3 className="font-bold mb-8 mt-2 text-[#951D1D]">
                      {ADMISSION_FORM_TEXT.TUITION_FEE.TITLE}
                    </h3>
                    <div className="align-middle mb-0.5 ml-6 ">
                      <span>{ADMISSION_FORM_TEXT.TUITION_FEE.PROVIDE_1}</span>
                      <Select
                        id="tuitionAssistantshipType"
                        name="tuitionAssistantshipType"
                        disabled={isViewMode}
                        options={tuitionAssistantshipOptions}
                        value={formik.values.tuitionAssistantshipType ?? ''}
                        InputLabelProps={{ shrink: true }}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          Boolean(formik.touched.tuitionAssistantshipType) &&
                          Boolean(formik.errors.tuitionAssistantshipType)
                        }
                        helperText={
                          formik.touched.tuitionAssistantshipType &&
                          formik.errors.tuitionAssistantshipType
                        }
                        sx={{
                          width: '14rem',
                          margin: '-0.75rem 0.5rem',
                        }}
                      />
                      <span>{ADMISSION_FORM_TEXT.TUITION_FEE.PROVIDE_2}</span>
                    </div>
                    {provideTuition && (
                      <div className="align-middle mb-0.5 ml-6 mt-10">
                        <span>{ADMISSION_FORM_TEXT.ACCOUNT_CODE}</span>
                        <TextField
                          size="small"
                          id="tuitionFeeAccountCode"
                          name="tuitionFeeAccountCode"
                          disabled={isViewMode}
                          value={formik.values.tuitionFeeAccountCode}
                          InputLabelProps={{ shrink: true }}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            Boolean(formik.touched.tuitionFeeAccountCode) &&
                            Boolean(formik.errors.tuitionFeeAccountCode)
                          }
                          helperText={
                            formik.touched.tuitionFeeAccountCode &&
                            formik.errors.tuitionFeeAccountCode
                          }
                          style={{
                            width: 150,
                            ...commonInsertTextFieldStyle,
                          }}
                        />
                      </div>
                    )}
                  </Box>
                )}
                {/* 3.  stipend */}
                {isApproved && (
                  <Box>
                    <h3
                      className={`font-bold my-8 ${
                        needStipend ? 'text-[#951D1D]' : 'text-[#9E9E9E]'
                      }`}
                    >
                      {ADMISSION_FORM_TEXT.STIPEND.TITLE}
                    </h3>
                    {needStipend && (
                      <Box>
                        <RadioGroup
                          name="stipendApproval"
                          value={formik.values.stipendApproval}
                          onChange={formik.handleChange}
                          className="mx-6"
                        >
                          <Radio
                            value={'true'}
                            disabled={isViewMode}
                            error={
                              formik.touched.stipendApproval &&
                              Boolean(formik.errors.stipendApproval)
                            }
                            size="small"
                            label={
                              <div className="text-sm leading-loose">
                                {ADMISSION_FORM_TEXT.STIPEND.OPTION_1}
                              </div>
                            }
                            sx={{
                              ...commonSvgIconStyle,
                              paddingY: '0.25rem',
                            }}
                          />
                          <RadioWithTextField
                            value={'false'}
                            disabled={isViewMode}
                            error={
                              formik.touched.stipendApproval &&
                              Boolean(formik.errors.stipendApproval)
                            }
                            size="small"
                            label={
                              <div className="text-sm leading-loose">
                                {ADMISSION_FORM_TEXT.STIPEND.OPTION_2}
                              </div>
                            }
                            sx={{
                              ...commonSvgIconStyle,
                              paddingY: '0.25rem',
                            }}
                            textFieldProps={{
                              className: 'mb-4 !mt-4',
                              id: 'rejectionRemarks',
                              name: 'rejectionRemarks',
                              disabled: isViewMode,
                              value:
                                formik.values.rejectionRemarks ?? undefined,
                              InputLabelProps: { shrink: true },
                              onChange: formik.handleChange,
                              onBlur: formik.handleBlur,
                              error:
                                Boolean(formik.touched.rejectionRemarks) &&
                                Boolean(formik.errors.rejectionRemarks),

                              helperText:
                                formik.touched.rejectionRemarks &&
                                formik.errors.rejectionRemarks,

                              rows: 6,
                              multiline: true,
                              label: 'Remarks(if any)',
                              sx: {
                                width: '90%',
                                marginTop: '2rem',
                                marginLeft: '3rem',
                                marginBottom: '1.5rem',
                              },
                            }}
                          />
                        </RadioGroup>
                      </Box>
                    )}
                  </Box>
                )}

                {/* 4.  Plagiarism Checking */}
                {isApproved && (
                  <div>
                    <h3 className="font-bold my-8 text-[#951D1D]">
                      {ADMISSION_FORM_TEXT.PLAGIARISM.TITLE}
                    </h3>
                    <div className="flex flex-col gap-[24px] px-[1.5em]">
                      <Checkbox
                        id="plagiarismCheck"
                        label={
                          <div className="text-[14px] mt-[6px] font-semibold">
                            {ADMISSION_FORM_TEXT.PLAGIARISM.QUESTION_1}
                          </div>
                        }
                        size="small"
                        disabled={isViewMode}
                        name="plagiarismCheck"
                        value={formik.values.plagiarismCheck}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.plagiarismCheck &&
                          Boolean(formik.errors.plagiarismCheck)
                        }
                        sx={{
                          alignItems: 'flex-start',
                        }}
                      />
                      <Checkbox
                        disabled={isViewMode}
                        size="small"
                        label={
                          <div className="text-[14px] mt-[6px] font-semibold">
                            {ADMISSION_FORM_TEXT.PLAGIARISM.QUESTION_2}
                          </div>
                        }
                        value={formik.values.plagiarismNotFound}
                        name="plagiarismNotFound"
                        id="plagiarismNotFound"
                        onChange={formik.handleChange}
                        error={
                          formik.touched.plagiarismNotFound &&
                          Boolean(formik.errors.plagiarismNotFound)
                        }
                        sx={{
                          alignItems: 'flex-start',
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* 5.  Conflict of Interest */}
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  alignItems={'center'}
                  marginTop={10}
                >
                  <Checkbox
                    className="mb-2"
                    disabled={isViewMode}
                    size="small"
                    label={ADMISSION_FORM_TEXT.SUPERVISOR_TEAM_CHECK_2}
                    value={formik.values.confirmationDoRPgSInTeam}
                    name="confirmationDoRPgSInTeam"
                    id="confirmationDoRPgSInTeam"
                    onChange={formik.handleChange}
                  />
                  <Checkbox
                    disabled={isViewMode}
                    label={ADMISSION_FORM_TEXT.SUPERVISOR_TEAM_CHECK_1}
                    value={formik.values.confirmationChairInTeam}
                    name="confirmationChairInTeam"
                    id="confirmationChairInTeam"
                    size="small"
                    onChange={formik.handleChange}
                  />
                </Box>

                {/* 6.  Save Button */}
                {!isViewMode && (
                  <ButtonContainer
                    paddingTop={'2rem'}
                    spacing={1}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    marginBottom={10}
                  >
                    <Button
                      variant="contained"
                      className="!mr-4"
                      color="primary"
                      type="submit"
                    >
                      {PAGE_TEXT.SAVE}
                    </Button>
                    <Button onClick={onJumpToNext}>{PAGE_TEXT.NEXT}</Button>
                  </ButtonContainer>
                )}
              </Box>
            </Stack>
          </form>
        }
      />
    </div>
  );
};
